import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { toast } from 'sonner';
import {
  History, RefreshCw, Search, Trash2, RotateCcw, Plus, Edit, AlertTriangle,
  User, Calendar, Filter, X, LogIn, ShieldAlert, KeyRound, Power, ShieldOff, FileX, Activity, Database,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_META = {
  soft_delete: { label: 'Soft Delete', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Trash2 },
  bulk_soft_delete: { label: 'Bulk Soft Delete', color: 'bg-amber-100 text-amber-700', icon: Trash2 },
  restore: { label: 'Restore', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: RotateCcw },
  bulk_restore: { label: 'Bulk Restore', color: 'bg-emerald-100 text-emerald-700', icon: RotateCcw },
  permanent_delete: { label: 'Hapus Permanen', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: AlertTriangle },
  bulk_permanent_delete: { label: 'Bulk Hapus Permanen', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  auto_purge: { label: 'Auto-purge', color: 'bg-slate-100 text-slate-700', icon: Trash2 },
  create: { label: 'Create', color: 'bg-blue-100 text-blue-700', icon: Plus },
  update: { label: 'Update', color: 'bg-indigo-100 text-indigo-700', icon: Edit },
  login_success: { label: 'Login Berhasil', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: LogIn },
  login_failed: { label: 'Login Gagal', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: ShieldAlert },
  change_password: { label: 'Ganti Password', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300', icon: KeyRound },
  customer_change_password: { label: 'Ganti Password (Customer)', color: 'bg-violet-100 text-violet-700', icon: KeyRound },
  reset_password: { label: 'Reset Password', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', icon: KeyRound },
  suspend_tenant: { label: 'Suspend Tenant', color: 'bg-red-100 text-red-700', icon: Power },
  activate_tenant: { label: 'Activate Tenant', color: 'bg-emerald-100 text-emerald-700', icon: Power },
  bulk_delete_tenants: { label: 'Bulk Delete Tenants', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  reset_database: { label: 'Reset Database', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: Database },
  reset_all_invoices: { label: 'Reset Semua Tagihan', color: 'bg-red-100 text-red-700', icon: FileX },
  reset_all_payments: { label: 'Reset Semua Pembayaran', color: 'bg-red-100 text-red-700', icon: FileX },
  delete_all_invoices: { label: 'Delete All Invoices', color: 'bg-red-100 text-red-700', icon: FileX },
  manual_isolir_run: { label: 'Manual Isolir', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: ShieldOff },
  manual_unisolir: { label: 'Manual Unisolir', color: 'bg-emerald-100 text-emerald-700', icon: Activity },
  delete_old_logs: { label: 'Hapus Log Lama', color: 'bg-slate-100 text-slate-700', icon: Trash2 },
  cascade_soft_delete_backfill: { label: 'Cascade Backfill', color: 'bg-amber-100 text-amber-700', icon: Trash2 },
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: '200' });
      if (filterAction) params.set('action', filterAction);
      if (filterEntity) params.set('entity_type', filterEntity);
      if (filterUser) params.set('user', filterUser);
      const r = await axios.get(`${API_URL}/api/v2/audit-logs?${params.toString()}`, { headers });
      if (r.data?.success) setLogs(r.data.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat audit logs');
    } finally {
      setLoading(false);
    }
  }, [days, filterAction, filterEntity, filterUser]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/v2/audit-logs/stats?days=${days}`, { headers });
      if (r.data?.success) setStats(r.data.data);
    } catch (e) { console.error(e); }
  }, [days]);

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  const fmtDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'medium' });
  };

  const renderChanges = (changes) => {
    if (!changes) return null;
    let parsed = changes;
    if (typeof changes === 'string') {
      try { parsed = JSON.parse(changes); } catch { return <span className="text-xs text-slate-500 dark:text-slate-400">{changes}</span>; }
    }
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-[10px] text-slate-500 dark:text-slate-400 font-mono mt-1">
        {Object.entries(parsed).slice(0, 3).map(([k, v]) => (
          <span key={k} className="mr-2">
            <span className="text-slate-400 dark:text-slate-500">{k}:</span> <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
          </span>
        ))}
      </div>
    );
  };

  const clearFilters = () => {
    setFilterAction(''); setFilterEntity(''); setFilterUser(''); setPage(1);
  };

  // Client-side filter & pagination
  const filteredLogs = useMemo(() => {
    return (logs || []).filter(l => {
      if (filterAction && l.action !== filterAction) return false;
      if (filterEntity && l.entity_type !== filterEntity) return false;
      if (filterUser && !(l.username || '').toLowerCase().includes(filterUser.toLowerCase())) return false;
      return true;
    });
  }, [logs, filterAction, filterEntity, filterUser]);

  const totalItems = filteredLogs.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedLogs = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, safePage, pageSize]);

  // Reset to page 1 when filters/days change
  useEffect(() => { setPage(1); }, [filterAction, filterEntity, filterUser, days, pageSize]);

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-7xl mx-auto" data-testid="audit-logs-page">
      <div className="flex items-center gap-2">
        <History className="w-6 h-6 text-indigo-500" />
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Audit Log</h1>
        <Badge className="bg-slate-100 text-slate-700 ml-auto dark:bg-slate-800 dark:text-slate-200">{stats?.total || 0} aktivitas / {days} hari</Badge>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-3 bg-white dark:bg-slate-900 border-0 shadow-sm">
            <div className="text-[10px] uppercase text-slate-500 mb-1 dark:text-slate-400">Total Aktivitas</div>
            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{stats.total}</div>
          </Card>
          <Card className="p-3 bg-white dark:bg-slate-900 border-0 shadow-sm">
            <div className="text-[10px] uppercase text-slate-500 mb-1 dark:text-slate-400">Hapus (soft+permanent)</div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {(stats.by_action || []).filter(a => a.action.includes('delete') || a.action === 'auto_purge').reduce((s, a) => s + a.count, 0)}
            </div>
          </Card>
          <Card className="p-3 bg-white dark:bg-slate-900 border-0 shadow-sm">
            <div className="text-[10px] uppercase text-slate-500 mb-1 dark:text-slate-400">Restore</div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {(stats.by_action || []).filter(a => a.action.includes('restore')).reduce((s, a) => s + a.count, 0)}
            </div>
          </Card>
          <Card className="p-3 bg-white dark:bg-slate-900 border-0 shadow-sm">
            <div className="text-[10px] uppercase text-slate-500 mb-1 dark:text-slate-400">User Paling Aktif</div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-200 truncate">
              {stats.top_users?.[0]?.username || '-'}
              <span className="text-xs text-slate-500 ml-1 dark:text-slate-400">({stats.top_users?.[0]?.count || 0})</span>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-3 bg-white dark:bg-slate-900 border-0 shadow-sm">
        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="w-4 h-4 text-slate-400 dark:text-slate-500" />
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="text-xs border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800"
            data-testid="filter-days"
          >
            <option value={1}>24 jam</option>
            <option value={7}>7 hari</option>
            <option value={30}>30 hari</option>
            <option value={90}>90 hari</option>
          </select>
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="text-xs border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800"
            data-testid="filter-action"
          >
            <option value="">Semua aksi</option>
            <option value="soft_delete">Soft Delete</option>
            <option value="bulk_soft_delete">Bulk Soft Delete</option>
            <option value="restore">Restore</option>
            <option value="bulk_restore">Bulk Restore</option>
            <option value="permanent_delete">Hapus Permanen</option>
            <option value="auto_purge">Auto-purge</option>
            <option value="login_success">Login Berhasil</option>
            <option value="login_failed">Login Gagal</option>
          </select>
          <select
            value={filterEntity}
            onChange={(e) => setFilterEntity(e.target.value)}
            className="text-xs border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800"
            data-testid="filter-entity"
          >
            <option value="">Semua entity</option>
            <option value="customer">Customer</option>
            <option value="owner">Owner</option>
            <option value="auth">Login/Auth</option>
            <option value="invoice">Invoice</option>
            <option value="payment">Payment</option>
          </select>
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              placeholder="Filter by user..."
              className="pl-8 h-8 text-xs"
              data-testid="filter-user"
            />
          </div>
          {(filterAction || filterEntity || filterUser) && (
            <Button onClick={clearFilters} size="sm" variant="ghost" className="h-8 text-xs">
              <X className="w-3 h-3 mr-1" />Clear
            </Button>
          )}
          <Button onClick={() => { fetchLogs(); fetchStats(); }} size="sm" variant="outline" className="h-8 text-xs ml-auto">
            <RefreshCw className="w-3 h-3 mr-1" />Refresh
          </Button>
        </div>
      </Card>

      {/* Logs list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 dark:text-slate-500">
          <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Tidak ada aktivitas pada periode ini</p>
        </Card>
      ) : (
        <Card className="bg-white dark:bg-slate-900 border-0 shadow-sm overflow-hidden">
          <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="audit-logs-list">
            {paginatedLogs.map(log => {
              const meta = ACTION_META[log.action] || { label: log.action, color: 'bg-slate-100 text-slate-700', icon: History };
              const Icon = meta.icon;
              return (
                <li key={log.id} className="p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                        <span className="text-xs text-slate-500 dark:text-slate-400">·</span>
                        <span className="text-xs font-medium text-slate-700 dark:text-slate-300 capitalize">{log.entity_type}</span>
                        {log.entity_name && (
                          <>
                            <span className="text-xs text-slate-500 dark:text-slate-400">·</span>
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{log.entity_name}</span>
                          </>
                        )}
                        {log.entity_id && (
                          <span className="text-[10px] text-slate-400 font-mono dark:text-slate-500">#{log.entity_id}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-1 dark:text-slate-400">
                        <span><User className="w-3 h-3 inline mr-0.5" />{log.username || 'system'}</span>
                        <span><Calendar className="w-3 h-3 inline mr-0.5" />{fmtDate(log.created_at)}</span>
                        {log.ip_address && <span className="font-mono">{log.ip_address}</span>}
                      </div>
                      {renderChanges(log.changes)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <Pagination
            page={safePage}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </Card>
      )}
    </div>
  );
};

export default AuditLogs;
